import {
  createContext,
  createSignal,
  useContext,
  type Accessor,
  type JSX,
  type Component,
} from 'solid-js';

import { slotSkill, type DollSlot, type InventoryItem } from '../net/protocol';
import type { GameStore } from '../state/store';
import {
  commandsFor,
  dropRefusalText,
  resolveDrop,
  type Drag,
  type DropIntent,
  type DropTarget,
} from './drag';

/**
 * The one drag in flight, shared by every panel that can be a source or a target.
 *
 * **A context rather than a signal in each panel**, because a drag crosses panels:
 * bag → paper doll lives in two components, and bag → vault in two more. Two
 * independent drag states would mean an item picked up in one panel simply did not
 * exist in the other, which is the entire gesture.
 *
 * **A context rather than the store**, because the store is the seam between Babylon
 * and the HUD — it holds what the *server* said, plus what the render loop needs. A
 * half-finished pointer gesture is neither, and putting it there would mean the render
 * loop could see it.
 */
/**
 * A skill on the cursor, on its way to an action-bar slot.
 *
 * **Deliberately not folded into `Drag`.** An item drag carries a container, a grabbed
 * cell and a footprint, and it resolves through `resolveDrop`'s six cases; a skill has
 * none of those — it has an id and one kind of destination. Widening `Drag` into a union
 * would have put a discriminant into every one of those six cases, the ghost, the grid,
 * the paper doll and the vault, to describe a gesture none of them can receive.
 *
 * They share this controller rather than a second provider, because only one gesture can
 * be in flight at a time and two providers is one more thing to forget to mount.
 */
export interface SkillDrag {
  skillId: string;
  /** What the ghost says. Carried rather than looked up: the catalogue is the panel's. */
  name: string;
  /** Where the pointer is, in viewport coordinates. */
  x: number;
  y: number;
}

export interface DragController {
  /** The drag in flight, or null. */
  drag: Accessor<Drag | null>;
  /**
   * The skill being dragged to the action bar, or null.
   *
   * Skills could only be slotted by clicking a row and then clicking a slot number, which
   * is the one arrangement in the HUD where the obvious gesture did nothing. Asked for
   * directly: *allow to drag and drop a skill to its toolbar.*
   */
  skillDrag: Accessor<SkillDrag | null>;
  /** Which action-bar slot the pointer is over during a skill drag, or null. */
  hoveredSlot: Accessor<number | null>;
  /**
   * What the pointer is over, or null. Drives the placement highlight.
   *
   * A cell is always a *bag* cell — the bag is the only container with cells — so there
   * is no container to name. The vault hovers as itself, with no coordinates, because a
   * list has none.
   */
  hovered: Accessor<HoverTarget | null>;
  /**
   * The client's own answer to a drop it refused itself, or null.
   *
   * Set here rather than by whichever component happened to receive the release,
   * because a drop can be resolved by a grid cell, a paper-doll socket or the
   * window-level "released over nothing" listener — three places, one sentence. It is
   * deliberately **not** the action bar's notice: that corner is for what the *server*
   * said, and mixing the two would make it impossible to tell an answer from a guess.
   */
  notice: Accessor<string | null>;
  /**
   * The item the pointer is resting on anywhere in the HUD, or null.
   *
   * Shared rather than local because the *answer* to hovering an item is drawn somewhere
   * else: the paper doll lights the sockets the item could go into, and a bag cell has no
   * way to reach the doll. Asked for directly — *the hovering should put an overlay over
   * the slots of equipment it fits*.
   *
   * Distinct from `hovered`, which is a **drop target** during a drag and is about where a
   * gesture would land. This is about what is being *read*, and it is set with no gesture
   * in flight.
   */
  inspecting: Accessor<InventoryItem | null>;

  /** Picks a skill up, on its way to the action bar. */
  beginSkill(skillId: string, name: string, at: { x: number; y: number }): void;
  /** Notes which action-bar slot the pointer is over, or clears it. */
  hoverSlot(slot: number | null): void;
  /** Notes the item being read, so the doll can light the sockets it fits. */
  inspect(item: InventoryItem | null): void;
  /**
   * Drops the skill on a slot, sending `CMD_SLOT_SKILL`, and reports whether it landed.
   *
   * `null` is a release over anything that is not a slot, which cancels — the same rule an
   * item drop over the world follows. **Not a refusal**: putting a skill down where there
   * is no slot is a change of mind, not a mistake, so nothing is said about it.
   */
  dropSkill(slot: number | null): boolean;

  /** Picks an item up. `cell` is which of *its own* cells was grabbed. */
  begin(
    item: InventoryItem,
    origin: Drag['origin'],
    cell: { col: number; row: number },
    at: { x: number; y: number },
  ): void;
  /** Moves the ghost. Called from a pointer move on the window. */
  moveTo(at: { x: number; y: number }): void;
  /** Notes what the pointer is over, or clears it. */
  hover(target: HoverTarget | null): void;
  /**
   * Resolves the drag against a target, sends whatever it means, and returns what that
   * was.
   *
   * The intent comes back rather than just a message because the caller needs it: a
   * drop resolving to `NO_MOVEMENT` **is a click**, and the grid turns that into an
   * activation. Null when there was no drag to resolve.
   */
  drop(target: DropTarget): DropIntent | null;
  /** Abandons the drag without sending anything. */
  cancel(): void;
}

/**
 * What the pointer is over during a drag.
 *
 * The same two shapes as the acting half of `DropTarget`, and deliberately a separate
 * type: hovering `elsewhere` is what `null` means, and hovering a doll socket needs no
 * entry because a socket highlights itself.
 */
export type HoverTarget = { kind: 'cell'; col: number; row: number } | { kind: 'vault' };

/**
 * Exported so a spec can hand a controller it *holds a reference to* down the tree.
 *
 * `<DragProvider>` builds its own, which is right for the HUD and useless for a test that
 * has to push a drag in from outside — the skills panel and the action bar are different
 * components, and the bar's half of the gesture cannot be exercised without driving the
 * controller directly.
 */
export const DragCtx = createContext<DragController>();

/**
 * Builds a controller over a store.
 *
 * Exported so a spec can make one directly, and so `Hud` can make exactly one.
 */
export function createDragController(store: GameStore): DragController {
  const [drag, setDrag] = createSignal<Drag | null>(null);
  const [skillDrag, setSkillDrag] = createSignal<SkillDrag | null>(null);
  const [hovered, setHovered] = createSignal<HoverTarget | null>(null);
  const [hoveredSlot, setHoveredSlot] = createSignal<number | null>(null);
  const [notice, setNotice] = createSignal<string | null>(null);
  const [inspecting, setInspecting] = createSignal<InventoryItem | null>(null);

  return {
    drag,
    skillDrag,
    hovered,
    hoveredSlot,
    notice,
    inspecting,

    beginSkill(skillId, name, at) {
      // Only one gesture at a time. An item already on the cursor is abandoned rather
      // than resolved: the player has plainly started something else, and silently
      // completing a half-finished item move would be the surprising outcome.
      setDrag(null);
      setSkillDrag({ skillId, name, x: at.x, y: at.y });
      setHovered(null);
      setHoveredSlot(null);
      setNotice(null);
    },

    hoverSlot(slot) {
      setHoveredSlot(slot);
    },

    dropSkill(slot) {
      const current = skillDrag();
      setSkillDrag(null);
      setHoveredSlot(null);
      if (current === null || slot === null) return false;
      // The server owns the outcome, as always: this names the intent. Slotting a skill
      // already on the bar *moves* it — that rule is the server's and this does not
      // duplicate it.
      store.send(slotSkill(slot, current.skillId));
      return true;
    },

    begin(item, origin, cell, at) {
      setDrag({ item, origin, grab: cell, x: at.x, y: at.y });
      setHovered(null);
    },

    moveTo(at) {
      // Whichever gesture is in flight; only one ever is.
      setDrag((current) => (current === null ? null : { ...current, x: at.x, y: at.y }));
      setSkillDrag((current) => (current === null ? null : { ...current, x: at.x, y: at.y }));
    },

    hover(target) {
      setHovered(target);
    },

    inspect(item) {
      setInspecting(item);
    },

    drop(target) {
      const current = drag();
      setDrag(null);
      setHovered(null);
      if (current === null) return null;

      const intent = resolveDrop(current, target);
      // The commands go out in the order `commandsFor` returns them — a reslot is two,
      // and taking the item off has to reach the server before putting it on.
      for (const cmd of commandsFor(intent)) store.send(cmd);
      // A cancelled drag clears the last message rather than leaving it: `dropRefusalText`
      // returns null for the two outcomes that are not failures, so this is one line for
      // both saying something and stopping.
      setNotice(intent.kind === 'nothing' ? dropRefusalText(intent.reason) : null);
      return intent;
    },

    cancel() {
      setDrag(null);
      setSkillDrag(null);
      setHovered(null);
      setHoveredSlot(null);
      setNotice(null);
      // Not `inspecting`: cancelling a drag leaves the pointer wherever it is, and the
      // item under it is still the item being read. Clearing it here would blank the
      // doll's highlight while the tooltip it belongs to is still on screen.
    },
  };
}

export const DragProvider: Component<{ store: GameStore; children: JSX.Element }> = (props) => (
  <DragCtx.Provider value={createDragController(props.store)}>{props.children}</DragCtx.Provider>
);

/**
 * The shared controller.
 *
 * Throws when there is no provider, deliberately: the failure mode of a silent
 * per-component fallback is a drag that works inside one panel and vanishes between
 * two, which looks like a layout bug and is not one. A spec renders its component
 * inside `<DragProvider>`, which is one line and makes the sharing explicit.
 */
export function useDrag(): DragController {
  const ctx = useContext(DragCtx);
  if (!ctx) {
    throw new Error('useDrag() outside a <DragProvider>: a drag is shared between panels');
  }
  return ctx;
}

/** Re-exported for callers that only need the types. */
export type { Drag, DropIntent, DropTarget, DollSlot };
